/* ============================================================================
   Primitivas de gráfica: escalas, ticks y geometría.
   Sin dependencias. Todo el dibujo es SVG generado a mano para poder cumplir al
   pie de la letra las especificaciones de marca (línea de 2px, extremos
   redondeados de 4px, separación de 2px en color de superficie, anillo de 2px
   en los marcadores).
   ========================================================================== */

import { niceTicks, periodIndex, type Period } from '../lib/format'

export interface Scale {
  (value: number): number
  domain: readonly [number, number]
  range: readonly [number, number]
  invert(px: number): number
}

export function linearScale(
  domain: readonly [number, number],
  range: readonly [number, number],
): Scale {
  const [d0, d1] = domain
  const [r0, r1] = range
  const span = d1 - d0
  const fn = ((value: number) => {
    if (span === 0) return (r0 + r1) / 2
    return r0 + ((value - d0) / span) * (r1 - r0)
  }) as Scale
  fn.domain = domain
  fn.range = range
  fn.invert = (px: number) => {
    const rSpan = r1 - r0
    if (rSpan === 0) return d0
    return d0 + ((px - r0) / rSpan) * span
  }
  return fn
}

/** Margen interior del área de trazo. La banda del eje X va INCLUIDA en el alto. */
export interface Insets {
  top: number
  right: number
  bottom: number
  left: number
}

export const DEFAULT_INSETS: Insets = { top: 16, right: 16, bottom: 26, left: 48 }

export interface Extent {
  min: number
  max: number
}

/**
 * Dominio Y. Para series de dinero NO forzamos el cero: la variación mensual de
 * la canasta es de unos cuantos puntos porcentuales y anclar en cero aplanaría
 * la señal. En cambio dejamos aire arriba y abajo, y el eje siempre rotula sus
 * valores para que la escala truncada sea evidente.
 */
export function niceExtent(values: number[], opts: { zeroBased?: boolean; pad?: number } = {}): Extent {
  const finite = values.filter((v) => Number.isFinite(v))
  if (finite.length === 0) return { min: 0, max: 1 }
  let min = Math.min(...finite)
  let max = Math.max(...finite)
  if (opts.zeroBased) min = Math.min(0, min)
  if (min === max) {
    const bump = Math.abs(min) * 0.05 || 1
    return { min: min - bump, max: max + bump }
  }
  const pad = (max - min) * (opts.pad ?? 0.12)
  return { min: min - pad, max: max + pad }
}

export function yTicks(extent: Extent, count = 4): number[] {
  return niceTicks(extent.min, extent.max, count)
}

/**
 * Ticks del eje X para una serie mensual. Devuelve índices dentro de `periods`.
 * Regla: siempre se rotula el primero y el último; en medio se eligen los
 * cambios de año cuando caben, y si no, un muestreo uniforme.
 */
export function monthTicks(periods: Period[], maxTicks: number): number[] {
  const n = periods.length
  if (n === 0) return []
  if (n <= maxTicks) return periods.map((_, i) => i)

  const yearStarts: number[] = []
  for (let i = 0; i < n; i++) {
    const p = periods[i]!
    if (p.endsWith('-01')) yearStarts.push(i)
  }

  if (yearStarts.length >= 2 && yearStarts.length <= maxTicks) {
    const set = new Set(yearStarts)
    // El último punto se rotula sólo si su año no está ya en el eje: si no,
    // aparecían dos ticks «2026» pegados al borde derecho.
    const lastStart = yearStarts[yearStarts.length - 1]
    const lastYear = periods[n - 1]?.slice(0, 4)
    const lastStartYear = lastStart === undefined ? undefined : periods[lastStart]?.slice(0, 4)
    if (lastYear !== undefined && lastYear !== lastStartYear) set.add(n - 1)
    return Array.from(set).sort((a, b) => a - b)
  }

  /*
     Con más años que ticks disponibles, submuestreamos los INICIOS DE AÑO, no
     posiciones arbitrarias. Muestrear posiciones daba etiquetas como
     «2016 2017 2018 2020 2021 2022 2024», que se leen como años faltantes en los
     datos cuando en realidad la serie está completa.
  */
  if (yearStarts.length > maxTicks) {
    const every = Math.ceil(yearStarts.length / maxTicks)
    const picked = yearStarts.filter((_, i) => i % every === 0)
    if (picked.length >= 2) return picked
  }

  const step = Math.max(1, Math.ceil((n - 1) / Math.max(1, maxTicks - 1)))
  const out: number[] = []
  for (let i = 0; i < n; i += step) out.push(i)
  if (out[out.length - 1] !== n - 1) out.push(n - 1)
  return out
}

/** Une periodos de varias series en un eje mensual continuo y ordenado. */
export function unionPeriods(seriesPeriods: Period[][]): Period[] {
  const set = new Set<Period>()
  for (const arr of seriesPeriods) for (const p of arr) set.add(p)
  return Array.from(set).sort((a, b) => periodIndex(a) - periodIndex(b))
}

/* --- Rutas SVG ----------------------------------------------------------- */

export interface Pt {
  x: number
  y: number
}

/** Polilínea recta. Honesta para datos mensuales: no inventa curvatura. */
export function linePath(points: Pt[]): string {
  if (points.length === 0) return ''
  let d = `M${round(points[0]!.x)} ${round(points[0]!.y)}`
  for (let i = 1; i < points.length; i++) {
    d += `L${round(points[i]!.x)} ${round(points[i]!.y)}`
  }
  return d
}

/** Relleno de área bajo la línea, cerrado en la base. */
export function areaPath(points: Pt[], baselineY: number): string {
  if (points.length === 0) return ''
  const first = points[0]!
  const last = points[points.length - 1]!
  let d = `M${round(first.x)} ${round(baselineY)}L${round(first.x)} ${round(first.y)}`
  for (let i = 1; i < points.length; i++) {
    d += `L${round(points[i]!.x)} ${round(points[i]!.y)}`
  }
  d += `L${round(last.x)} ${round(baselineY)}Z`
  return d
}

/**
 * Rectángulo con el extremo de dato redondeado 4px y escuadrado en la base.
 * `direction` dice de qué lado está el extremo de dato.
 */
export function barPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  direction: 'up' | 'down' | 'left' | 'right',
): string {
  const w = Math.max(0, width)
  const h = Math.max(0, height)
  if (w === 0 || h === 0) return ''
  const r = Math.max(0, Math.min(radius, direction === 'up' || direction === 'down' ? w / 2 : h / 2, direction === 'up' || direction === 'down' ? h : w))

  const X = round(x)
  const Y = round(y)
  const W = round(w)
  const H = round(h)
  const R = round(r)

  switch (direction) {
    case 'up':
      // extremo de dato arriba
      return `M${X} ${Y + H}L${X} ${Y + R}Q${X} ${Y} ${X + R} ${Y}L${X + W - R} ${Y}Q${X + W} ${Y} ${X + W} ${Y + R}L${X + W} ${Y + H}Z`
    case 'down':
      return `M${X} ${Y}L${X + W} ${Y}L${X + W} ${Y + H - R}Q${X + W} ${Y + H} ${X + W - R} ${Y + H}L${X + R} ${Y + H}Q${X} ${Y + H} ${X} ${Y + H - R}Z`
    case 'right':
      return `M${X} ${Y}L${X + W - R} ${Y}Q${X + W} ${Y} ${X + W} ${Y + R}L${X + W} ${Y + H - R}Q${X + W} ${Y + H} ${X + W - R} ${Y + H}L${X} ${Y + H}Z`
    case 'left':
      return `M${X + W} ${Y}L${X + R} ${Y}Q${X} ${Y} ${X} ${Y + R}L${X} ${Y + H - R}Q${X} ${Y + H} ${X + R} ${Y + H}L${X + W} ${Y + H}Z`
  }
}

function round(n: number): number {
  return Math.round(n * 100) / 100
}

/** Índice del dato más cercano a una posición en px del eje X. */
export function nearestIndex(scale: Scale, count: number, px: number): number {
  if (count === 0) return -1
  const raw = scale.invert(px)
  return Math.max(0, Math.min(count - 1, Math.round(raw)))
}

/** Interpola un color entre dos hex — usado sólo para rampas ya validadas. */
export function pickRampStep(ramp: readonly string[], t: number): string {
  if (ramp.length === 0) return 'currentColor'
  const clamped = Math.max(0, Math.min(1, t))
  const idx = Math.round(clamped * (ramp.length - 1))
  return ramp[idx]!
}
